import React, { Suspense, useState } from 'react';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Divider, Layout, Menu } from 'antd';
import cn from 'classnames';
import MenuItemContent from 'layouts/main/MenuItem/MenuItemContent';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import {
    SIDEBAR_ITEMS,
    SIDEBAR_WIDTH,
    SIDEBAR_WIDTH_COLLAPSED,
} from 'utils/constants/sidebar';
import type { TComponent } from 'utils/types/component';

import { ReactComponent as GrowardLogo } from 'assets/icons/growardLogo.svg';

import PageLoader from 'components/ui/PageLoader/PageLoader';

import { UserBadge } from './components/UserBadge';
import styles from './LayoutMain.module.scss';

const { Header, Content, Sider } = Layout;

const style = {
    margin: 0,
    width: '100%',
    borderRadius: 0,
    height: '64px',
    fontSize: '14px',
    fontWeight: 500,
};

const DEFAULT_SELECTED_ROUTE = '/merchants/balance';

const LayoutMain: TComponent = () => {
    const { t } = useTranslation();

    const getMenuItems = () => {
        return SIDEBAR_ITEMS.map(({ icon, to, title, subMenu }) => {
            if (subMenu) {
                return {
                    key: to,
                    icon: collapsed ? null : icon,
                    label: collapsed ? icon : t(title),
                    className: styles.subMenu,
                    children: subMenu.map((subItem) => ({
                        key: subItem.to,
                        label: (
                            <MenuItemContent
                                to={subItem.to}
                                label={t(subItem.title)}
                            />
                        ),
                        className: styles.item,
                    })),
                    style,
                };
            } else {
                return {
                    key: to,
                    label: (
                        <MenuItemContent
                            to={to}
                            icon={icon}
                            label={t(title)}
                            isCollapsed={collapsed}
                        />
                    ),
                    style: collapsed ? { ...style, display: 'flex' } : style,
                };
            }
        });
    };

    const [collapsed, setCollapsed] = useState(false);

    return (
        <>
            <Header className={styles.header}>
                <div className={styles.logo}>
                    <GrowardLogo role='img' aria-label='Groward' />
                </div>
                <UserBadge />
            </Header>
            <Layout hasSider>
                <Sider
                    theme='light'
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    className={styles.sidebar}
                    width={collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH}
                >
                    <div className={styles.sidebarContent}>
                        <Divider className={styles.divider} />
                        <Menu
                            items={getMenuItems()}
                            rootClassName={styles.menuContainer}
                            className={styles.menu}
                            mode='inline'
                            defaultSelectedKeys={[DEFAULT_SELECTED_ROUTE]}
                        />
                    </div>
                    <div
                        className={cn({
                            [styles.sidebarFooterCollapsed]: collapsed,
                            [styles.sidebarFooter]: !collapsed,
                        })}
                    >
                        {React.createElement(
                            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
                            {
                                className: styles.trigger,
                                onClick: () => setCollapsed(!collapsed),
                            },
                        )}
                    </div>
                </Sider>
                <Layout>
                    <Content className={styles.content}>
                        <Suspense fallback={<PageLoader />}>
                            <Outlet />
                        </Suspense>
                    </Content>
                </Layout>
            </Layout>
        </>
    );
};

export { LayoutMain };
